import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import classNames from 'classnames';

import { Box, TableCell, TableRow, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import EditUserDialog from './components/EditUserDialog';
import DeleteUserDialog from './components/DeleteUserDialog';

import { UserApi, AccessClaims } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { getRoleText } from './utils';
import errorHandler from 'common/services/errorHandler';

import { useDispatch } from 'react-redux';
import * as NotificationActions from 'actions/NotificationActions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getShopRoles } from 'common/modules/users/utils';
import { Callable } from 'common/frontend/callable';

export const ROW_HEIGHT = 63;

interface Props {
	user: UserApi;
	accessClaims: AccessClaims;
	disableDelete: boolean;
	disableEdit: boolean;
	isOnlyAdmin?: boolean;
	isSelf?: boolean;
}

const UsersTableRow = (props: Props) => {
	const dispatch = useDispatch();
	const { user, disableDelete, disableEdit, isOnlyAdmin, accessClaims, isSelf } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const activeShopId = useSelector(ShopSelectors.activeShopId)!;
	const locationsById = useSelector(ShopSelectors.allShopLocationsById);

	const { roles: _roles, permissions, locations } = accessClaims;
	const roles = getShopRoles(_roles);

	const deleteUser = async (userId: string) => {
		try {
			await Callable.users.deleteUserFromShop({ shopId: activeShopId, userId });
			dispatch(
				NotificationActions.showNotification({
					variant: 'info',
					message: t('common:actions.userDeleted', 'User deleted'),
				}),
			);
		} catch (error) {
			dispatch(
				NotificationActions.showNotification({
					variant: 'error',
					message: t('common:errors.somethingWentWrong'),
				}),
			);
			errorHandler.report(error);
		}
	};

	const editUserAccess = async (updatedUser: UserApi) => {
		const { id } = updatedUser;
		const accessClaims = updatedUser?.access?.[activeShopId];
		if (!isEmpty(accessClaims)) {
			try {
				await Callable.users.setUserAccess({ shopId: activeShopId, userId: id, accessClaims });
				dispatch(
					NotificationActions.showNotification({
						variant: 'info',
						message: t('common:actions.changesSaved', 'Changes saved'),
					}),
				);
			} catch (error) {
				dispatch(
					NotificationActions.showNotification({
						variant: 'error',
						message: t('common:errors.somethingWentWrong'),
					}),
				);
				errorHandler.report(error);
			}
		}
	};

	const getLocationsText = (locations: string[] | undefined) => {
		if (!locations) {
			return t('common:shopLocation.allStores');
		}
		if (locations.length === 1) {
			const locationId = locations[0];
			const locationName = locationsById[locationId]?.name ?? t('users.deletedStore');
			return locationName;
		}

		return t('common:shopLocation.nStores', {
			defaultValue: '{{amount}} stores',
			amount: locations.length,
		});
	};

	return (
		<>
			<TableRow className={classes.tableRow}>
				<TableCell>
					<Typography variant="body2" color="secondary">
						{user.email} {isSelf ? `(${t('users.you', 'You')})` : ''}
					</Typography>
				</TableCell>
				<TableCell>
					<Box className={classes.role}>
						<Typography variant="body2" color="secondary">
							{roles.map(getRoleText).join(', ')}
						</Typography>
						{!!permissions.length && (
							<Typography variant="caption" color="secondary" className={classes.permission}>
								+{' '}
								{t('users.permissionCount', {
									defaultValue: '{{count}} permission',
									count: permissions.length,
								})}
							</Typography>
						)}
					</Box>
				</TableCell>
				<TableCell>
					<Typography variant="body2" color="secondary">
						{getLocationsText(locations)}
					</Typography>
				</TableCell>
				<TableCell>
					<Box className={classes.actions}>
						<Typography
							variant="body2"
							color="primary"
							className={classNames(classes.linkText, {
								[classes.linkTextDisabled]: disableEdit,
							})}
							onClick={() => (disableEdit ? null : setEditDialogOpen(true))}
						>
							{t('common:actions.edit')}
						</Typography>
						<Box mr={4} />
						<Typography
							variant="body2"
							color="primary"
							className={classNames(classes.linkText, {
								[classes.linkTextDisabled]: disableDelete || isOnlyAdmin,
							})}
							onClick={() => (disableDelete || isOnlyAdmin ? null : setDeleteDialogOpen(true))}
						>
							{t('common:actions.delete')}
						</Typography>
					</Box>
				</TableCell>
			</TableRow>
			{editDialogOpen && (
				<EditUserDialog
					open={editDialogOpen}
					value={user}
					onChange={editUserAccess}
					onClose={() => setEditDialogOpen(false)}
					isOnlyAdmin={isOnlyAdmin}
				/>
			)}
			{deleteDialogOpen && (
				<DeleteUserDialog
					open={deleteDialogOpen}
					value={user}
					onDelete={deleteUser}
					onClose={() => setDeleteDialogOpen(false)}
				/>
			)}
		</>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		tableRow: {
			'& > *': {
				borderBottom: `1px solid ${theme.palette.border.table}`,
				whiteSpace: 'nowrap',
				height: ROW_HEIGHT,
				'&:first-child': {
					paddingLeft: theme.spacing(4),
				},
				'&:last-child': {
					paddingRight: theme.spacing(4),
				},
			},
		},
		role: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			maxWidth: 200,
		},
		permission: {
			marginLeft: theme.spacing(1.5),
			padding: theme.spacing(0.5, 1.5),
			borderRadius: 16,
			border: `1px solid ${theme.palette.border.inputOutline}`,
		},
		actions: {
			display: 'flex',
			justifyContent: 'flex-end',
			width: 100,
		},
		linkText: {
			textDecoration: 'underline',
			cursor: 'pointer',
			width: 112,
		},
		linkTextDisabled: {
			cursor: 'default',
			color: theme.palette.action.disabled,
		},
		button: {
			minHeight: 32,
			minWidth: 88,
			fontSize: '1.2rem',
			fontWeight: 500,
		},
	}),
);

export default UsersTableRow;
